import type {
  WebsiteReplayChunkType,
  WebsiteReplayCredentialsType,
  WebsiteSessionActivitySecondsType,
  WebsiteSessionEventNameType,
  WebsiteSessionEventType,
} from './session-replay.model';

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MAX_HEARTBEAT_SECONDS = 90;

export function getWebsitePagePath(): string {
  return (
    window.location.pathname + window.location.search + window.location.hash
  );
}

/** Next.js navigation, query updates and browser back/forward all use History. */
export function observeWebsiteNavigation(onNavigate: () => void): () => void {
  const { history } = window;
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;
  const pushState: History['pushState'] = function (...args) {
    originalPushState.apply(history, args);
    onNavigate();
  };
  const replaceState: History['replaceState'] = function (...args) {
    originalReplaceState.apply(history, args);
    onNavigate();
  };
  history.pushState = pushState;
  history.replaceState = replaceState;
  window.addEventListener('popstate', onNavigate);
  window.addEventListener('hashchange', onNavigate);
  return () => {
    if (history.pushState === pushState) history.pushState = originalPushState;
    if (history.replaceState === replaceState)
      history.replaceState = originalReplaceState;
    window.removeEventListener('popstate', onNavigate);
    window.removeEventListener('hashchange', onNavigate);
  };
}

export function getOrCreateStoredUuid(
  storage: Storage,
  key: string,
  createUuid: () => string = () => crypto.randomUUID()
): string {
  const existing = storage.getItem(key);
  if (existing && UUID_PATTERN.test(existing)) return existing;
  const uuid = createUuid();
  storage.setItem(key, uuid);
  return uuid;
}

export class WebsiteSessionEventQueue {
  private events: WebsiteSessionEventType[] = [];
  private readonly createUuid: () => string;
  private readonly now: () => Date;
  private readonly maxPending: number;

  constructor(
    createUuid: () => string = () => crypto.randomUUID(),
    now: () => Date = () => new Date(),
    maxPending = 200
  ) {
    this.createUuid = createUuid;
    this.now = now;
    this.maxPending = maxPending;
  }

  add(
    type: WebsiteSessionEventNameType,
    value: string | null,
    data: Record<string, unknown> | null = null
  ): void {
    this.events.push({
      customer_device_session_event_uuid: this.createUuid(),
      customer_device_session_event_type: type,
      customer_device_session_event_value: value,
      customer_device_session_event_data_json: data,
      customer_device_session_event_client_time: this.now().toISOString(),
    });
    if (this.events.length > this.maxPending) {
      this.events.splice(0, this.events.length - this.maxPending);
    }
  }

  batch(limit = 50): WebsiteSessionEventType[] {
    return this.events.slice(0, limit);
  }

  clear(): void {
    this.events = [];
  }

  acknowledge(events: WebsiteSessionEventType[]): void {
    const acknowledged = new Set(
      events.map(event => event.customer_device_session_event_uuid)
    );
    this.events = this.events.filter(
      event => !acknowledged.has(event.customer_device_session_event_uuid)
    );
  }
}

export class WebsiteReplayChunkQueue {
  private readonly maxBufferedBytes: number;
  private readonly maxBufferedEvents: number;
  private active: WebsiteReplayCredentialsType | null = null;
  private bufferedEvents: string[] = [];
  private bufferedBytes = 0;
  private pendingChunks: WebsiteReplayChunkType[] = [];

  constructor(maxBufferedBytes: number, maxBufferedEvents: number) {
    this.maxBufferedBytes = maxBufferedBytes;
    this.maxBufferedEvents = maxBufferedEvents;
  }

  activate(credentials: WebsiteReplayCredentialsType): boolean {
    if (
      this.active?.customer_replay_uuid === credentials.customer_replay_uuid
    ) {
      this.active.customer_replay_upload_token =
        credentials.customer_replay_upload_token;
      this.active.customer_replay_next_sequence = Math.max(
        this.active.customer_replay_next_sequence,
        credentials.customer_replay_next_sequence
      );
      return false;
    }

    this.flush();
    this.active = { ...credentials };
    return true;
  }

  credentials(): Pick<
    WebsiteReplayCredentialsType,
    'customer_replay_uuid' | 'customer_replay_upload_token'
  > | null {
    if (!this.active) return null;
    return {
      customer_replay_uuid: this.active.customer_replay_uuid,
      customer_replay_upload_token: this.active.customer_replay_upload_token,
    };
  }

  add(event: string): boolean {
    if (!this.active) return false;
    this.bufferedEvents.push(event);
    this.bufferedBytes += event.length;
    if (
      this.bufferedBytes >= this.maxBufferedBytes ||
      this.bufferedEvents.length >= this.maxBufferedEvents
    ) {
      this.flush();
      return true;
    }
    return false;
  }

  flush(): void {
    if (!this.active || this.bufferedEvents.length === 0) return;
    this.pendingChunks.push({
      customer_replay_uuid: this.active.customer_replay_uuid,
      customer_replay_upload_token: this.active.customer_replay_upload_token,
      customer_replay_sequence: this.active.customer_replay_next_sequence++,
      events: this.bufferedEvents,
    });
    this.bufferedEvents = [];
    this.bufferedBytes = 0;
  }

  firstPending(): WebsiteReplayChunkType | null {
    return this.pendingChunks[0] ?? null;
  }

  acknowledge(chunk: WebsiteReplayChunkType): void {
    if (
      this.pendingChunks[0]?.customer_replay_uuid ===
        chunk.customer_replay_uuid &&
      this.pendingChunks[0]?.customer_replay_sequence ===
        chunk.customer_replay_sequence
    ) {
      this.pendingChunks.shift();
    }
  }

  clear(): void {
    this.active = null;
    this.bufferedEvents = [];
    this.bufferedBytes = 0;
    this.pendingChunks = [];
  }
}

export class SessionActivityAccumulator {
  private lastAccountingTime: number;
  private active = false;
  private visible: boolean;
  private activeMs = 0;
  private visibleMs = 0;

  constructor(startTime: number, visible: boolean) {
    this.lastAccountingTime = startTime;
    this.visible = visible;
  }

  setActive(active: boolean, now: number): void {
    this.account(now);
    this.active = active;
  }

  setVisible(visible: boolean, now: number): void {
    this.account(now);
    this.visible = visible;
  }

  snapshotSeconds(now: number): WebsiteSessionActivitySecondsType {
    this.account(now);
    return {
      activeSeconds: Math.min(
        MAX_HEARTBEAT_SECONDS,
        Math.floor(this.activeMs / 1000)
      ),
      visibleSeconds: Math.min(
        MAX_HEARTBEAT_SECONDS,
        Math.floor(this.visibleMs / 1000)
      ),
    };
  }

  acknowledgeSeconds(seconds: WebsiteSessionActivitySecondsType): void {
    this.activeMs = Math.max(
      0,
      this.activeMs - Math.max(0, seconds.activeSeconds) * 1000
    );
    this.visibleMs = Math.max(
      0,
      this.visibleMs - Math.max(0, seconds.visibleSeconds) * 1000
    );
  }

  private account(now: number): void {
    if (now < this.lastAccountingTime) return;
    const elapsed = now - this.lastAccountingTime;
    if (this.visible) {
      this.visibleMs += elapsed;
      if (this.active) this.activeMs += elapsed;
    }
    this.lastAccountingTime = now;
  }
}
